import { fmtInt, parseTs } from '../../utils/statsFormat';
import { useChartTooltip, useElementWidth } from './useChartLayout';
import ChartTooltip from './ChartTooltip';
import ScaleLegend, { seqOpacity } from './ScaleLegend';
import './charts.css';

/**
 * Day × hour activity grid.
 *
 * Extracted from Statistics.js HourHeatmap. It was rendered in a 320px grid
 * cell, giving 24 hour-columns about 11px each — unreadable. Cells are now
 * sized from the measured width so the grid fills whatever room the card has,
 * and it ships a scale legend since the encoding is colour alone.
 */
function HeatmapGrid({ cells = [], maxValue = 0, valueName = 'positions' }) {
  const [wrapRef, width] = useElementWidth(760);
  const { tip, show, hide } = useChartTooltip();

  const dates = [...new Set(cells.map(c => c.date))].sort();
  if (dates.length === 0) return <div className="chart-card-empty">No data</div>;

  const byKey = new Map(cells.map(c => [`${c.date}|${c.hour}`, c.positions]));
  const max = Math.max(1, maxValue || 0, ...cells.map(c => c.positions || 0));

  const labelW = 92;
  const bottomH = 22;
  const topPad = 4;
  // Fill the available width rather than using a fixed cell size.
  const cellW = Math.max(12, Math.floor((width - labelW - 6) / 24));
  const cellH = Math.max(14, Math.min(30, Math.round(cellW * 0.78)));
  const gridW = cellW * 24;
  const height = topPad + dates.length * cellH + bottomH;

  const dayLabel = (iso) => {
    const d = parseTs(iso);
    return d
      ? `${d.toLocaleDateString(undefined, { weekday: 'short' })} ${iso.slice(5).replace('-', '/')}`
      : iso;
  };

  // Thin the hour labels if the cells are narrow.
  const hourStep = cellW >= 30 ? 2 : cellW >= 20 ? 3 : 6;

  const onEnter = (date, hour, v, evt) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    show({
      x: rect ? evt.clientX - rect.left : 0,
      y: rect ? evt.clientY - rect.top : 0,
      title: `${dayLabel(date)} ${String(hour).padStart(2, '0')}:00 UTC`,
      rows: [{ value: fmtInt(v), name: valueName }],
    });
  };

  return (
    <div className="chart-plot" ref={wrapRef}>
      <svg className="chart-svg" width="100%" height={height}
           viewBox={`0 0 ${labelW + gridW + 6} ${height}`} role="img"
           onPointerLeave={hide}>
        {dates.map((date, r) => (
          <g key={date}>
            <text x={labelW - 9} y={topPad + r * cellH + cellH - 6}
                  className="chart-axis-label" textAnchor="end">{dayLabel(date)}</text>
            {Array.from({ length: 24 }, (_, h) => {
              const v = byKey.get(`${date}|${h}`) || 0;
              return (
                <rect key={h}
                      x={labelW + h * cellW + 1} y={topPad + r * cellH + 1}
                      width={cellW - 2} height={cellH - 2} rx="2"
                      fill={v > 0 ? 'var(--chart-seq)' : 'transparent'}
                      fillOpacity={seqOpacity(v / max)}
                      className="chart-cell"
                      tabIndex={v > 0 ? 0 : -1}
                      aria-label={`${dayLabel(date)} ${h}:00, ${fmtInt(v)} ${valueName}`}
                      onPointerEnter={(e) => onEnter(date, h, v, e)}
                      onPointerMove={(e) => onEnter(date, h, v, e)}
                      onFocus={(e) => onEnter(date, h, v, e)}
                      onBlur={hide} />
              );
            })}
          </g>
        ))}
        {Array.from({ length: 24 }, (_, h) => h).filter(h => h % hourStep === 0).map(h => (
          <text key={`hl${h}`} x={labelW + h * cellW + cellW / 2} y={height - 7}
                className="chart-axis-label" textAnchor="middle">
            {String(h).padStart(2, '0')}
          </text>
        ))}
      </svg>

      <ScaleLegend max={max} label={valueName} />
      <ChartTooltip tip={tip} containerWidth={width} />
    </div>
  );
}

export default HeatmapGrid;
